import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/index';
import { Input, Button, Space, Select } from 'antd'
import { validateEmail, validatePhone } from '../../assets/js/validators'

interface LoginPageProps {
    handleViewChange: (newView: string, data?: any) => void;
    data?: any;
}

interface loginInfo {
    loginName?: string;
    email?: string;
    phone?: string;
    password?: string;
    verification?: string;
    account?: string;
    emailError?: string;
    phoneError?: string;
    accountError?: string;
    passwordError?: string;
    verificationError?: string;
    enterAccount?: string;
    enterEmail?: string;
    enterPassword?: string;
    enterPasswordAgain?: string;
    enterVerification?: string;
    enterPhone?: string;
    forget?: string;
    getBack?: string;
    next?: string;
    check1?: string;
    verifyIdentity?: string;
    resetPassword?: string;
    orInfo?: string;
    backLogin?: string;
    seccond?: string;
    changeInfo?: string;
    sendVerification?:string;
    verifyIdentityFor?:string;
    loginType?: string;
}

// 定义登录方式枚举
enum LoginMethod {
    ACCOUNT = 0, // 账号登录
    EMAIL = 1,   // 邮箱登录
    PHONE = 2    // 手机登录
}

const LoginPage: React.FC<LoginPageProps> = ({ handleViewChange, data }) => {
    const themeColor = useSelector((state: RootState) => state.themeColor.value);
    const [activeMethod, setActiveMethod] = useState<LoginMethod>(LoginMethod.ACCOUNT); // 默认选中账号登录
    const [accountInput, setAccountInput] = useState('')
    const [emailInput, setEmailInput] = useState('')
    const [phoneInput, setPhoneInput] = useState('')
    const [error, setError] = useState<string | null | undefined>(null);

    // 直接使用中文翻译
    const [loginInfo] = useState<loginInfo>({
        loginName: "登录",
        email: "邮箱",
        phone: "手机",
        account: "账号",
        emailError: "请输入有效的邮箱地址",
        phoneError: "请输入有效的手机号码",
        accountError: "账号长度至少3位",
        passwordError: "请输入密码",
        verificationError: "请输入验证码",
        enterAccount: "请输入账号",
        enterEmail: "请输入邮箱",
        enterPassword: "请输入密码",
        enterPasswordAgain: "请再次输入密码",
        enterVerification: "请输入验证码",
        enterPhone: "请输入手机号",
        forget: "忘记密码",
        getBack: "找回密码",
        next: "下一步",
        check1: "请检查你的邮箱",
        verifyIdentity: "验证身份",
        resetPassword: "重置密码",
        orInfo: "或",
        backLogin: "返回登录",
        seccond: "秒",
        changeInfo: "修改信息",
        sendVerification: "发送验证码",
        verifyIdentityFor:  "验证身份以找回密码",
        loginType: "登录方式"
    });

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        
        // 根据当前激活的登录方式更新对应的输入值
        switch (activeMethod) {
            case LoginMethod.ACCOUNT:
                setAccountInput(value);
                break;
            case LoginMethod.EMAIL:
                setEmailInput(value);
                break;
            case LoginMethod.PHONE:
                setPhoneInput(value);
                break;
        }
        setError(null);
    };

    const handleNext = () => {
        let isValid = true;
        let newError = null;

        // 验证输入
        switch (activeMethod) {
            case LoginMethod.ACCOUNT:
                if (accountInput.length < 3) {
                    isValid = false;
                    newError = loginInfo.accountError;
                }
                break;
            case LoginMethod.EMAIL:
                if (!validateEmail(emailInput)) {
                    isValid = false;
                    newError = loginInfo.emailError;
                }
                break;
            case LoginMethod.PHONE:
                if (!validatePhone(phoneInput)) {
                    isValid = false;
                    newError = loginInfo.phoneError;
                }
                break;
        }

        if (!isValid) {
            setError(newError);
            return;
        }

        // 准备跳转数据
        let payload = {};
        let nextView = '';
        
        switch (activeMethod) {
            case LoginMethod.ACCOUNT:
                payload = { account: accountInput };
                nextView = 'accountLogin';
                break;
            case LoginMethod.EMAIL:
                payload = { email: emailInput };
                nextView = 'emailVerification';
                break;
            case LoginMethod.PHONE:
                payload = { phone: phoneInput };
                nextView = 'phoneVerification';
                break;
        }

        handleViewChange(nextView, payload);
    };

    // 定义登录方式选项卡
    const loginMethods = [
        { 
            key: LoginMethod.ACCOUNT,
            name: loginInfo.account || "账号", 
            disabled: false,
            placeholder: loginInfo.enterAccount || "请输入账号"
            // tooltip: "邮箱登录暂时不可用" 

        },
        // { 
        //     key: LoginMethod.EMAIL,
        //     name: loginInfo.email || "邮箱", 
        //     disabled: true,
        //     placeholder: loginInfo.enterEmail || "请输入邮箱",
        //     tooltip: "邮箱登录暂时不可用" 
        // },
        // { 
        //     key: LoginMethod.PHONE,
        //     name: loginInfo.phone || "手机", 
        //     disabled: true,
        //     placeholder: loginInfo.enterPhone || "请输入手机号",
        //     tooltip: "手机登录暂时不可用" 
        // },
    ];

    const phoneOptions = [
        { value: '+86', label: '+86' },
        { value: '+72', label: '+72' },
    ];

    // 检查当前输入是否有效
    const isInputValid = () => {
        switch (activeMethod) {
            case LoginMethod.ACCOUNT: 
                return accountInput.trim() === '';
            case LoginMethod.EMAIL: 
                return emailInput.trim() === '';
            case LoginMethod.PHONE: 
                return phoneInput.trim() === '';
            default: 
                return true;
        }
    };

    // 获取当前输入框的值
    const getCurrentInputValue = () => {
        switch (activeMethod) {
            case LoginMethod.ACCOUNT: 
                return accountInput;
            case LoginMethod.EMAIL: 
                return emailInput;
            case LoginMethod.PHONE: 
                return phoneInput;
            default: 
                return '';
        }
    };

    // 渲染输入框
    const renderInputField = () => {
        const currentMethod = loginMethods.find(m => m.key === activeMethod);
        
        if (!currentMethod) return null;

        switch (activeMethod) {
            case LoginMethod.ACCOUNT:
                return (
                    <Input 
                        placeholder={currentMethod.placeholder}
                        status={error ? 'error' : ''}
                        value={accountInput}
                        onChange={handleInputChange}
                        disabled={currentMethod.disabled}
                        className={currentMethod.disabled ? "disabled-input" : ""}
                    />
                );
            case LoginMethod.EMAIL:
                return (
                    <Input 
                        placeholder={currentMethod.placeholder}
                        status={error ? 'error' : ''}
                        value={emailInput}
                        onChange={handleInputChange}
                        disabled={currentMethod.disabled}
                        className="disabled-input"
                    />
                );
            case LoginMethod.PHONE:
                return (
                    <Space.Compact>
                        <Select 
                            defaultValue="+86" 
                            options={phoneOptions} 
                            disabled={currentMethod.disabled} 
                        />
                        <Input 
                            className='phoneInput disabled-input' 
                            status={error ? 'error' : ''} 
                            value={phoneInput} 
                            placeholder={currentMethod.placeholder}
                            onChange={handleInputChange}
                            disabled={currentMethod.disabled}
                        />
                    </Space.Compact>
                );
            default:
                return null;
        }
    };

    return (
        <div>
            <div className='login-header'>{loginInfo.loginName}</div>
            
            
            <div className='login-text'>
                {loginMethods.map((method, index) => (
                    <span 
                        key={method.key}
                        className={`login-switch ${method.disabled ? 'disabled' : ''}`}
                        onClick={() => {
                            if (!method.disabled) {
                                setActiveMethod(method.key);
                                setError(null);
                            }
                        }}
                        style={{
                            color: activeMethod === method.key ? themeColor : method.disabled ? '#ccc' : 'inherit',
                            cursor: method.disabled ? 'not-allowed' : 'pointer',
                            opacity: method.disabled ? 0.6 : 1,
                            position: 'relative',
                            marginRight: index < loginMethods.length - 1 ? '40px' : '0'
                        }}
                        // title={method.tooltip || ''}
                    >
                        {method.name}
                        {method.disabled && (
                            <span style={{
                                fontSize: '10px',
                                color: '#999',
                                marginLeft: '4px'
                            }}>
                            </span>
                        )}
                    </span>
                ))}
            </div>

            <div>
                {renderInputField()}
                
                {error && (
                    <div style={{ 
                        color: '#e60d0d', 
                        marginTop: '2px', 
                        fontSize: '12px' 
                    }}>
                        {error}
                    </div>
                )}
                
                {activeMethod !== LoginMethod.ACCOUNT && (
                    <div style={{ 
                        color: '#ff6b6b', 
                        marginTop: '10px', 
                        fontSize: '12px',
                        padding: '8px',
                        backgroundColor: '#fff5f5',
                        borderRadius: '4px',
                        border: '1px solid #ffe6e6'
                    }}>
                        ⚠️ 此登录方式暂时不可用，请使用账号登录
                    </div>
                )}
            </div>

            <Button 
                type="primary" 
                onClick={handleNext} 
                disabled={isInputValid() || activeMethod !== LoginMethod.ACCOUNT}
                style={{
                    opacity: activeMethod !== LoginMethod.ACCOUNT ? 0.5 : 1,
                    cursor: activeMethod !== LoginMethod.ACCOUNT ? 'not-allowed' : 'pointer'
                }}
            >
                {activeMethod !== LoginMethod.ACCOUNT ? '此方式已禁用' : loginInfo.next}
            </Button>

            {/* 添加底部信息提示 */}
            <div style={{
                marginTop: '20px',
                fontSize: '12px',
                color: '#666',
                textAlign: 'center'
            }}>
                {/* <p>提示：目前仅支持账号登录，邮箱和手机登录功能正在维护中</p> */}
            </div>
        </div>
    );
};

export default LoginPage;